/**
 * SidebarContent
 *
 * Split-panel container showing Explorer (top) and Scripts (bottom)
 * simultaneously. A draggable divider adjusts the height split.
 * The Scripts panel has a header bar with view controls.
 */

import { ReactNode, useCallback, useRef } from 'react';
import { FileCode2, PanelBottomClose, PanelTopClose, SplitSquareVertical } from 'lucide-react';
import {
  useSidebarStore,
  MIN_SPLIT_RATIO,
  MAX_SPLIT_RATIO,
  DEFAULT_SPLIT_RATIO,
} from '../../features/database-objects/store/sidebarStore';
import { ScriptBrowserPanel } from '../../features/script-browser/components/ScriptBrowserPanel';
import { Button } from '../../components/ui/button';
import { useSplitterDrag } from '../hooks/useSplitterDrag';
import { useAnalytics } from '../../core/analytics/useAnalytics';

/** Height of a panel header bar in pixels */
const HEADER_HEIGHT = 28;

/** Minimum panel content height in pixels (prevents dragging to invisibly small) */
const MIN_PANEL_HEIGHT = 80;

/** Keyboard resize step for the Explorer/Scripts ratio (Shift = 4x). */
const SPLIT_KEY_STEP = 0.02;
const SPLIT_KEY_STEP_BIG = 0.08;

/** Whether a candidate split ratio keeps both the Explorer and Scripts panels
 *  at or above `MIN_PANEL_HEIGHT` for the given available height. Shared by
 *  the drag path (`ratioForClientY`) and the keyboard path (`onKeyStep`) —
 *  fix round 1, Finding 2 — so the two can't silently disagree on the
 *  minimum the way two independently-maintained copies eventually would. */
function isRatioWithinPanelBounds(ratio: number, availableHeight: number): boolean {
  const explorerHeight = ratio * availableHeight;
  const scriptsHeight = (1 - ratio) * availableHeight;
  return explorerHeight >= MIN_PANEL_HEIGHT && scriptsHeight >= MIN_PANEL_HEIGHT;
}

interface SidebarContentProps {
  explorer: ReactNode;
}

export function SidebarContent({ explorer }: SidebarContentProps) {
  const splitRatio = useSidebarStore((s) => s.sidebarSplitRatio);
  const setSplitRatio = useSidebarStore((s) => s.setSplitRatio);
  const explorerCollapsed = useSidebarStore((s) => s.explorerCollapsed);
  const scriptsCollapsed = useSidebarStore((s) => s.scriptsCollapsed);
  const toggleExplorerCollapsed = useSidebarStore((s) => s.toggleExplorerCollapsed);
  const toggleScriptsCollapsed = useSidebarStore((s) => s.toggleScriptsCollapsed);

  const containerRef = useRef<HTMLDivElement>(null);

  const isSplitView = !explorerCollapsed && !scriptsCollapsed;

  // Explorer/Scripts divider (CH-13): pointer-capture + rAF drag, keyboard
  // step, double-click reset, via the shared `useSplitterDrag` hook (also
  // used by the sidebar seam and the vertical-tabs column seam). The ratio
  // math below is unchanged from the pre-existing mouse-drag handler —
  // only the event plumbing moved to pointer capture — so the resize feel
  // is identical, just no longer droppable on a fast or out-of-window drag.
  const { track } = useAnalytics();
  const dragStartClientYRef = useRef(0);
  const dragContainerRectRef = useRef<{ top: number; height: number } | null>(null);

  /** Ratio for a given absolute clientY, clamped to both the ratio bounds
   *  and the minimum panel heights. Returns null when the resize would
   *  violate either (caller should then leave the ratio unchanged). */
  const ratioForClientY = useCallback((clientY: number): number | null => {
    const rect = dragContainerRectRef.current;
    if (!rect) return null;
    const totalFixedHeight = HEADER_HEIGHT + 4;
    const availableHeight = rect.height - totalFixedHeight;
    if (availableHeight <= 0) return null;
    const mouseY = clientY - rect.top;
    let ratio = mouseY / availableHeight;
    ratio = Math.max(MIN_SPLIT_RATIO, Math.min(MAX_SPLIT_RATIO, ratio));
    return isRatioWithinPanelBounds(ratio, availableHeight) ? ratio : null;
  }, []);

  const divider = useSplitterDrag({
    axis: 'y',
    onDrag: (deltaPx) => {
      const ratio = ratioForClientY(dragStartClientYRef.current + deltaPx);
      if (ratio !== null) setSplitRatio(ratio);
    },
    onReset: () => {
      setSplitRatio(DEFAULT_SPLIT_RATIO);
      track('feature:splitter_reset', { surface: 'scripts-split' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? SPLIT_KEY_STEP_BIG : SPLIT_KEY_STEP;
      const nextRatio = Math.max(MIN_SPLIT_RATIO, Math.min(MAX_SPLIT_RATIO, splitRatio + direction * step));
      const container = containerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const availableHeight = rect.height - (HEADER_HEIGHT + 4);
        if (availableHeight > 0 && !isRatioWithinPanelBounds(nextRatio, availableHeight)) return;
      }
      setSplitRatio(nextRatio);
    },
  });

  const handleDividerPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      dragStartClientYRef.current = e.clientY;
      const container = containerRef.current;
      dragContainerRectRef.current = container
        ? { top: container.getBoundingClientRect().top, height: container.getBoundingClientRect().height }
        : null;
      divider.onPointerDown(e);
    },
    [divider]
  );

  const handleMinimizeScripts = useCallback(() => {
    if (!scriptsCollapsed) toggleScriptsCollapsed();
    if (explorerCollapsed) toggleExplorerCollapsed();
  }, [explorerCollapsed, scriptsCollapsed, toggleExplorerCollapsed, toggleScriptsCollapsed]);

  const handleMaximizeScripts = useCallback(() => {
    if (scriptsCollapsed) toggleScriptsCollapsed();
    if (!explorerCollapsed) toggleExplorerCollapsed();
  }, [explorerCollapsed, scriptsCollapsed, toggleExplorerCollapsed, toggleScriptsCollapsed]);

  const handleSplitView = useCallback(() => {
    if (scriptsCollapsed) toggleScriptsCollapsed();
    if (explorerCollapsed) toggleExplorerCollapsed();
  }, [explorerCollapsed, scriptsCollapsed, toggleExplorerCollapsed, toggleScriptsCollapsed]);

  const shouldShowExplorer = !explorerCollapsed;
  const shouldShowScripts = !scriptsCollapsed;

  return (
    <div ref={containerRef} className="h-full flex flex-col" data-testid="sidebar-split-panel">
      {/* Explorer content */}
      {shouldShowExplorer && (
        <div
          className="min-h-0 overflow-hidden"
          style={{
            flex: isSplitView
              ? `0 0 calc((100% - ${HEADER_HEIGHT + 4}px) * ${splitRatio})`
              : '1 1 0%',
          }}
        >
          {explorer}
        </div>
      )}

      {/* Draggable divider — only interactive when Explorer and Scripts are
          visible. CH-13: same 1px `--divider` line + ~6px invisible hit
          area as the sidebar seam and the vertical-tabs column seam (hit
          area grows via `before:` padding rather than painting the whole
          strip); hover and active-drag pinned to the identical `--primary`
          tint. `role="separator"` + keyboard resize + double-click reset
          only apply in split view — collapsed to a plain 1px rule
          otherwise, matching the pre-existing non-interactive state. */}
      {shouldShowExplorer && (
        <div
          className={
            isSplitView
              ? 'relative h-1.5 cursor-row-resize shrink-0 bg-transparent before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:h-px before:bg-divider before:transition-colors before:content-[\'\'] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background'
              : 'h-px bg-border shrink-0'
          }
          role={isSplitView ? 'separator' : undefined}
          aria-orientation={isSplitView ? 'horizontal' : undefined}
          aria-label={isSplitView ? 'Resize explorer and scripts panels' : undefined}
          aria-valuenow={isSplitView ? Math.round(splitRatio * 100) : undefined}
          aria-valuemin={isSplitView ? Math.round(MIN_SPLIT_RATIO * 100) : undefined}
          aria-valuemax={isSplitView ? Math.round(MAX_SPLIT_RATIO * 100) : undefined}
          aria-valuetext={isSplitView ? `${Math.round(splitRatio * 100)}% explorer` : undefined}
          tabIndex={isSplitView ? 0 : undefined}
          data-dragging={isSplitView && divider.isDragging ? 'true' : undefined}
          onPointerDown={isSplitView ? handleDividerPointerDown : undefined}
          onDoubleClick={isSplitView ? divider.onDoubleClick : undefined}
          onKeyDown={isSplitView ? divider.onKeyDown : undefined}
          style={isSplitView ? { touchAction: 'none', userSelect: 'none' } : undefined}
          data-testid="sidebar-split-divider"
        />
      )}

      {/* Scripts header */}
      <ScriptsHeader
        mode={
          scriptsCollapsed
            ? 'minimized'
            : explorerCollapsed
              ? 'split'
              : 'together'
        }
        onMinimize={handleMinimizeScripts}
        onMaximize={handleMaximizeScripts}
        onSplit={handleSplitView}
      />

      {/* Scripts content */}
      {shouldShowScripts && (
        <div
          className="min-h-0 overflow-hidden"
          style={{
            flex: isSplitView
              ? `0 0 calc((100% - ${HEADER_HEIGHT + 4}px) * ${1 - splitRatio})`
              : '1 1 0%',
          }}
        >
          <ScriptBrowserPanel />
        </div>
      )}
    </div>
  );
}

type ScriptsHeaderMode = 'together' | 'split' | 'minimized';

type ScriptsHeaderProps = {
  mode: ScriptsHeaderMode;
  onSplit: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
};

function ScriptsHeader({ mode, onSplit, onMinimize, onMaximize }: ScriptsHeaderProps) {
  const rightActions =
    mode === 'together'
      ? [
          { label: 'Maximize', icon: PanelTopClose, onClick: onMaximize },
          { label: 'Minimize', icon: PanelBottomClose, onClick: onMinimize },
        ]
      : mode === 'split'
        ? [
            { label: 'Split', icon: SplitSquareVertical, onClick: onSplit },
            { label: 'Minimize', icon: PanelBottomClose, onClick: onMinimize },
          ]
        : [
            { label: 'Split', icon: SplitSquareVertical, onClick: onSplit },
            { label: 'Maximize', icon: PanelTopClose, onClick: onMaximize },
          ];

  return (
    <div
      className="flex items-center gap-1.5 px-2 text-xs font-medium text-muted-foreground bg-muted/40 border-b border-border shrink-0 select-none"
      style={{ height: `${HEADER_HEIGHT}px` }}
      data-testid="scripts-panel-header"
    >
      <FileCode2 className="h-3.5 w-3.5" />
      <span className="flex-1 text-left">Scripts</span>
      <div className="flex items-center gap-1">
        {rightActions.map((action) => (
          <Button
            key={action.label}
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={action.onClick}
            title={action.label}
            aria-label={action.label}
          >
            <action.icon className="h-4 w-4" />
          </Button>
        ))}
      </div>
    </div>
  );
}
